// React Imports
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// I18n
import { Translate } from 'react-redux-i18n';

// Spectrum Components
import Heading from '@react/react-spectrum/Heading';
import Button from '@react/react-spectrum/Button';
import {
  Table, TBody, TD, TH, THead, TR,
} from '@react/react-spectrum/Table';
import Well from '@react/react-spectrum/Well';
import Wait from '@react/react-spectrum/Wait';
import Checkmark from '@react/react-spectrum/Icon/Checkmark';
import Close from '@react/react-spectrum/Icon/Close';

// Actions
import { approveRequest, rejectRequest } from '../../actions/adminActions';

// Styles
import '../css/Admin.scss';

/**
 * Component for displaying the Admin page.
 *
 * This component displays the administration page for the primary contact to approve
 * or reject pending access requests.
 *
 * @component
 * @example
 *
 * return (
 *   <Admin />
 * )
 */
class Admin extends Component {
  // Handles the Approve button click
  onApproveClick = (requestId) => {
    const { approveRequestAction } = this.props;
    approveRequestAction(requestId);
  }

  // Handles the Reject button click
  onRejectClick = (requestId) => {
    const { rejectRequestAction } = this.props;
    rejectRequestAction(requestId);
  }

  // Renders all of the request rows.
  renderRequestRows = () => {
    const { requests } = this.props;

    // Conditionally renders buttons, a spinner, or success message
    const renderButtons = (obj) => {
      switch (obj.status) {
        case 'pending':
          return <Wait centered />;
        case 'approved':
          return (
            <div className="requestApproved">
              <Checkmark />
              <Translate value="workflow.admin.request_approved" />
            </div>
          );
        case 'rejected':
          return (
            <div className="requestRejected">
              <Close />
              <Translate value="workflow.admin.request_rejected" />
            </div>
          );
        default:
          return (
            <div>
              <Button className="btn-approve" variant="cta" onClick={() => this.onApproveClick(obj.recId)}>
                <Translate value="workflow.admin.approve_button" />
              </Button>
              <Button className="btn-reject" variant="warning" quiet onClick={() => this.onRejectClick(obj.recId)}>
                <Translate value="workflow.admin.reject_button" />
              </Button>
            </div>
          );
      }
    };

    // Returns all the rows
    return Object.keys(requests).map(
      (id) => {
        const requestObj = requests[id];
        return (
          <TR key={requestObj.recId} className="request-row">
            <TD className="requestUser">
              {requestObj.firstName}
              {' '}
              {requestObj.lastName}
            </TD>
            <TD>
              {requestObj.email}
            </TD>
            <TD>
              {requestObj.accountName}
            </TD>
            { }
            <TD className="actions">
              {renderButtons(requestObj)}
            </TD>
          </TR>
        );
      },
    );
  }

  // Renders the table or a message saying there are no requests.
  renderRequestTable = () => {
    const { requests } = this.props;

    if (Object.keys(requests).length) {
      return (
        <Table>
          <THead>
            <TH>
              <Translate value="workflow.admin.name_label" />
            </TH>
            <TH>
              <Translate value="workflow.admin.email_label" />
            </TH>
            <TH>
              <Translate value="workflow.admin.company_label" />
            </TH>
          </THead>
          <TBody>
            {this.renderRequestRows()}
          </TBody>
        </Table>
      );
    }
    return (
      <Well>
        <Translate value="workflow.admin.no_requests" />
      </Well>
    );
  }

  render() {
    return (
      <div>
        <div className="page-container">
          <Heading>
            <Translate value="workflow.admin.approval_header" />
          </Heading>
          <p>
            <Translate value="workflow.admin.approval_instructions" />
          </p>
          {this.renderRequestTable()}
          <p>
            <Translate value="workflow.admin.done_instructions" />
          </p>
        </div>
      </div>
    );
  }
}
Admin.propTypes = {
  /*
   * requests {Object} An object with all of the access requests as keys.
   */
  requests: PropTypes.objectOf(PropTypes.object),
  /*
   * approveRequestAction {Function} The redux action to approve a specific request
   * provided by mapDispatchToProps.
   */
  approveRequestAction: PropTypes.func.isRequired,
  /*
   * rejectRequestAction {Function} The redux action to reject a specific request
   * provided by mapDispatchToProps.
   */
  rejectRequestAction: PropTypes.func.isRequired,
};
Admin.defaultProps = {
  requests: {},
};

const mapStateToProps = (state) => ({
  requests: state.admin.requests,
});

const mapDispatchToProps = {
  approveRequestAction: approveRequest,
  rejectRequestAction: rejectRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(Admin);
